import { Models } from "@/api/types";
import {
  MembershipDetailsData,
  OrderDetailsData,
  OrderHistoryData,
  PackageDetailsData,
  PurchaseAnalysis,
} from "@/services/CatalogTypes";
import { getDiscountText } from "@/services/ListingHelperService";
import { formatCurrency } from "@/toolbox/Money";

export class AdonisMarkdownService {
  static formatListingOverviewAsMarkdown(
    items: Array<{
      id: string;
      title: string;
      type: "Treatment" | "Package";
      description: string | null;
    }>,
    maxItems = 200
  ): string {
    const limited = items.slice(0, maxItems);
    const sections = limited.map((n) => {
      const heading = `## ${n.title} (${n.type})`;
      const link = `/pdp/package/${n.id}`;
      const desc = n.description ? `\n\n${n.description}` : "";
      return `${heading}\n${link}${desc}`;
    });
    const omitted = items.length - limited.length;
    return [
      ...sections,
      // instructions for how to fetch more might be needed here
      // depends on how we tackle large lists in the future.
      omitted > 0 ? `...and ${omitted} more` : undefined,
    ]
      .filter(Boolean)
      .join("\n\n");
  }
  static formatAvailableDiscountsAsMarkdown(
    shopDiscounts: Models<"ShopDiscountModel">,
    promotions: Models<"PromotionModel">[],
    listings: Models<"ListingModel">[],
    memberships: Models<"MembershipModel">[]
  ): string {
    let markdown = "# Available Discounts & Promotions\n\n";

    const activeListingDiscounts = Object.entries(
      shopDiscounts?.listingDiscounts || {}
    ).filter(([, discount]) => Boolean(discount));

    if (activeListingDiscounts.length > 0) {
      markdown += "## Product Discounts\n\n";

      activeListingDiscounts.forEach(([listingId, discount]) => {
        const listing = listings?.find((l) => l.id === listingId);
        if (listing && discount) {
          const discountText = getDiscountText(discount);
          if (discountText) {
            markdown += `### ${listing.title}\n`;
            markdown += `- **Discount**: ${discountText}\n`;
            if (discount.title) {
              markdown += `- **Offer**: ${discount.title}\n`;
            }
            if (discount.isFromSiteWidePromo) {
              markdown += `- *Site-wide promotion*\n`;
            }
            markdown += "\n";
          }
        }
      });
    }

    const activeMembershipDiscounts = Object.entries(
      shopDiscounts?.membershipDiscounts || {}
    ).filter(([, discount]) => Boolean(discount));

    if (activeMembershipDiscounts.length > 0) {
      markdown += "## Membership Discounts\n\n";

      activeMembershipDiscounts.forEach(([membershipId, discount]) => {
        const membership = memberships?.find((m) => m.id === membershipId);
        if (membership && discount) {
          const discountText = getDiscountText(discount);
          if (discountText) {
            markdown += `### ${membership.name}\n`;
            markdown += `- **Discount**: ${discountText}\n`;
            if (discount.title) {
              markdown += `- **Offer**: ${discount.title}\n`;
            }
            if (discount.isFromSiteWidePromo) {
              markdown += `- *Site-wide promotion*\n`;
            }
            markdown += "\n";
          }
        }
      });
    }

    if (promotions && promotions.length > 0) {
      markdown += "## Your Personal Promotions & Rewards\n\n";

      promotions.forEach((promo) => {
        markdown += `### ${promo.title}\n`;

        if (promo.shortDescription) {
          markdown += `${promo.shortDescription}\n\n`;
        }

        if (promo.promoCodeModel?.code) {
          markdown += `- **Promo Code**: ${promo.promoCodeModel.code}\n`;
        }

        if (promo.expiresOn) {
          const expiryDate = new Date(promo.expiresOn);
          const formattedDate = expiryDate.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          });
          markdown += `- **Expires**: ${formattedDate}\n`;
        }

        if (promo.redeemedOn) {
          markdown += `- **Status**: Already redeemed\n`;
        } else if (promo.active && !promo.paused) {
          markdown += `- **Status**: Available to use\n`;
        }

        if (promo.isInStoreRedeemable) {
          markdown += `- **In-Store**: Can be redeemed in store\n`;
        }

        if (promo.disclaimer) {
          markdown += `- *${promo.disclaimer}*\n`;
        }

        markdown += "\n";
      });
    }

    if (
      activeListingDiscounts.length === 0 &&
      activeMembershipDiscounts.length === 0 &&
      (!promotions || promotions.length === 0)
    ) {
      markdown +=
        "*No active discounts or promotions available at this time.*\n";
    }

    return markdown;
  }

  static formatPackageDetailsAsMarkdown(data: PackageDetailsData): string {
    const {
      listing,
      packages,
      expectations,
      shopDiscounts,
      shopDiscountsListing,
    } = data;

    let markdown = `# ${listing.title}\n\n`;

    if (listing.description) {
      markdown += `${listing.description}\n\n`;
    }

    if (packages && packages.length > 0) {
      markdown += `## Treatment Packages\n\n`;
      packages.forEach((pkg) => {
        markdown += `### ${pkg.name}\n`;
        if (pkg.description) markdown += `${pkg.description}\n\n`;

        pkg.packagePrices.forEach((price) => {
          const cost = formatCurrency(price.price);
          const memberCost =
            price.membership && price.membershipPrice
              ? formatCurrency(price.membershipPrice)
              : null;
          const unit =
            price.quantity > 1
              ? pkg.packageUnitType.pluralUnitType
              : pkg.packageUnitType.singularUnitType;

          markdown += `- **${price.quantity} ${unit}**: ${cost}`;
          if (memberCost) markdown += ` (Member price: ${memberCost})`;
          markdown += "\n";
        });
        markdown += "\n";
      });
    }

    const generalDiscount = shopDiscounts?.listingDiscounts?.[listing.id];
    const packageDiscounts = shopDiscountsListing?.packageDiscounts;

    if (
      generalDiscount ||
      (packageDiscounts && Object.keys(packageDiscounts).length > 0)
    ) {
      markdown += `## Available Promotions\n\n`;

      if (generalDiscount) {
        const title = generalDiscount.title || "Special Offer";
        const discountText = getDiscountText(generalDiscount);
        if (discountText) {
          markdown += `**${title}**: ${discountText}\n`;
        }

        if (generalDiscount.isFromSiteWidePromo) {
          markdown += `*Site-wide promotion*\n`;
        }
        markdown += "\n";
      }

      if (packageDiscounts) {
        Object.entries(packageDiscounts).forEach(([pkgId, discount]) => {
          const pkgName =
            packages.find((p) => p.id === pkgId)?.name || `Package`;
          const title = discount.title || "Package Special";
          const discountText = getDiscountText(discount);
          if (discountText) {
            markdown += `**${title}** (${pkgName}): ${discountText}\n`;
          }
        });
        markdown += "\n";
      }
    }

    if (expectations && expectations.length > 0) {
      markdown += `## What to Expect\n\n`;
      expectations.forEach((exp) => {
        markdown += `**${exp.title}**\n`;
        if (exp.description) markdown += `${exp.description}\n\n`;
      });
    }

    const baseCost = formatCurrency(listing.baseCost);
    const memberCost = listing.membershipCost
      ? formatCurrency(listing.membershipCost)
      : null;

    markdown += `## Pricing Summary\n`;
    markdown += `- Base Price: ${baseCost}\n`;
    if (memberCost) {
      markdown += `- Member Price: ${memberCost}\n`;
    }

    return markdown;
  }

  static formatMembershipDetailsAsMarkdown(
    membershipData: MembershipDetailsData
  ): string {
    const { membership, membershipRewards, shopDiscounts } = membershipData;

    let markdown = `# ${membership.name} Membership\n`;

    if (membership.id) {
      markdown += `/pdp/membership/${membership.id}\n\n`;
    } else {
      markdown += `\n`;
    }

    if (membership.description) {
      markdown += `${membership.description}\n\n`;
    }

    // Pricing & billing cycle
    const priceValue = membership.monthlyCost ?? membership.billingPeriodCost;
    if (priceValue != null) {
      const priceText = formatCurrency(priceValue);
      markdown += `## Membership Pricing\n`;
      markdown += `**Monthly Cost**: ${priceText}\n\n`;
    }
    if (
      (membership.billingPeriodIntervalCount ?? 0) > 1 &&
      membership.billingPeriodInterval
    ) {
      markdown += `**Billing Cycle**: Every ${membership.billingPeriodIntervalCount} ${membership.billingPeriodInterval}\n\n`;
    }

    // Commitment
    if ((membership.commitment ?? 0) > 0) {
      const intervalCount =
        (membership.billingPeriodIntervalCount ?? 0) > 0
          ? membership.billingPeriodIntervalCount
          : 1;
      const intervalName =
        membership.billingPeriodInterval && intervalCount > 1
          ? membership.billingPeriodInterval
          : "month";
      const totalPeriods = membership.commitment * intervalCount;
      markdown += `**Commitment**: ${totalPeriods} ${intervalName}${
        totalPeriods === 1 ? "" : "s"
      } required\n\n`;
    }

    // Promotion
    const discount = shopDiscounts?.membershipDiscounts?.[membership.id];
    if (discount) {
      markdown += `## Current Promotion\n`;
      const title = discount.title || "Limited Time Offer";
      const discountText = getDiscountText(discount);
      if (discountText) markdown += `**${title}**: ${discountText}\n`;
      if (discount.isFromSiteWidePromo) markdown += `*Site-wide promotion*\n`;
      markdown += "\n";
    }

    // Explicit member-only benefits from the model
    const benefits = Array.isArray(membership.membershipBenefits)
      ? membership.membershipBenefits
      : [];
    if (benefits.length > 0) {
      markdown += `## Member-Only Benefits\n\n`;
      benefits.forEach((b) => {
        if (b?.description) markdown += `- ${b.description}\n`;
      });
      markdown += `\n`;
    }

    // Rewards breakdown (signup, recurring, milestones) + Beauty Bank
    if (Array.isArray(membershipRewards) && membershipRewards.length > 0) {
      const groups = membershipRewards;

      const isSignup = (rg: Models<"MembershipRewardGroupModel">): boolean => {
        const flex =
          rg.billingPeriodsBeforeFirstReward != null &&
          rg.billingPeriodInterval != null;
        return flex
          ? rg.billingPeriodsBeforeFirstReward === 0 &&
              rg.billingPeriodInterval === 0
          : rg.monthsBeforeFirstReward === 0 && rg.periodMonths === 0;
      };
      const isMilestone = (
        rg: Models<"MembershipRewardGroupModel">
      ): boolean => {
        const flex =
          rg.billingPeriodsBeforeFirstReward != null &&
          rg.billingPeriodInterval != null;
        return flex
          ? rg.billingPeriodsBeforeFirstReward > 0 &&
              rg.billingPeriodInterval === 0
          : rg.monthsBeforeFirstReward > 0 && rg.periodMonths === 0;
      };
      const isRecurring = (
        rg: Models<"MembershipRewardGroupModel">
      ): boolean => {
        if (rg.billingPeriodInterval != null)
          return rg.billingPeriodInterval > 0;
        return rg.periodMonths > 0;
      };
      const getPeriodCount = (
        rg: Models<"MembershipRewardGroupModel">
      ): number => {
        const intervalCount =
          (membership.billingPeriodIntervalCount ?? 0) > 0
            ? (membership.billingPeriodIntervalCount as number)
            : 1;
        if (rg.billingPeriodsBeforeFirstReward != null)
          return rg.billingPeriodsBeforeFirstReward * intervalCount;
        if (rg.monthsBeforeFirstReward != null)
          return rg.monthsBeforeFirstReward;
        return 0;
      };
      const listRewardItems = (
        rg: Models<"MembershipRewardGroupModel">,
        max = 5
      ): string => {
        const items = Array.isArray(rg.membershipRewards)
          ? rg.membershipRewards
          : [];
        const parts: string[] = [];
        for (let i = 0; i < Math.min(items.length, max); i++) {
          const it = items[i];
          if (!it) continue;
          const unitName =
            it.packageUnitType?.singularUnitType && it.quantity === 1
              ? it.packageUnitType.singularUnitType
              : it.packageUnitType?.pluralUnitType || "unit";
          const pkgName =
            it.packageListingTitle || it.packageName || "Treatment";
          parts.push(`${it.quantity} ${unitName} ${pkgName}`);
        }
        const remaining = (rg.membershipRewards?.length ?? 0) - parts.length;
        if (remaining > 0) parts.push(`...and ${remaining} more`);
        return parts.map((p) => `- ${p}`).join("\n");
      };

      // Beauty Bank group (credit with no items)
      const beautyBankGroup = groups.find(
        (rg) =>
          rg.beautyBank?.creditAmount != null &&
          (rg.membershipRewards?.length ?? 0) === 0
      );
      if (beautyBankGroup && beautyBankGroup.beautyBank) {
        const credit = beautyBankGroup.beautyBank.creditAmount || 0;
        markdown += `## Beauty Bank\n`;
        markdown += `Earn ${formatCurrency(
          credit,
          0
        )} as banked credit each cycle\n\n`;
      }

      const signupGroups = groups.filter(isSignup);
      if (signupGroups.length > 0) {
        markdown += `## Sign-up Bonus\n`;
        signupGroups.forEach((rg) => {
          const items = listRewardItems(rg);
          if (items) markdown += `${items}\n`;
        });
        markdown += `\n`;
      }

      const recurringGroups = groups.filter(isRecurring);
      markdown += `## Included Each Cycle\n`;
      if (recurringGroups.length > 0) {
        let hasRecurringContent = false;
        recurringGroups.forEach((rg) => {
          if (rg.hasSelectableRewards && rg.quantityPerPeriod != null) {
            markdown += `- Select ${rg.quantityPerPeriod} treatment(s) from a curated list\n`;
            hasRecurringContent = true;
          }
          const items = listRewardItems(rg);
          if (items) {
            markdown += `${items}\n`;
            hasRecurringContent = true;
          }
        });
        if (!hasRecurringContent) {
          markdown += `- No recurring inclusions are supported for this membership\n`;
        }
        markdown += `\n`;
      } else {
        markdown += `- No recurring inclusions are supported for this membership\n\n`;
      }

      const milestoneGroups = groups.filter(isMilestone);
      if (milestoneGroups.length > 0) {
        markdown += `## Milestone Bonuses\n`;
        milestoneGroups.forEach((rg) => {
          const periodCount = getPeriodCount(rg);
          const items = listRewardItems(rg);
          markdown += `After ${periodCount} cycle(s):\n`;
          if (items) markdown += `${items}\n`;
        });
        markdown += `\n`;
      }
    }

    return markdown;
  }

  static formatOrderDetailsAsMarkdown(data: OrderDetailsData): string {
    const { order, packages, memberships } = data;
    const date = new Date(order.date).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });

    let markdown = `# Order Details - #${order.orderNumber}\n\n`;
    markdown += `**Date**: ${date}\n`;
    if (order.platformLocationName) {
      markdown += `**Location**: ${order.platformLocationName}\n`;
    }
    markdown += `\n`;

    if (order.packageLineItems.length > 0) {
      markdown += `## Treatments/Packages\n`;
      order.packageLineItems.forEach((item) => {
        const pkg = packages.find((p) => p.id === item.packageId);
        markdown += `### ${pkg?.name || item.displayName}\n`;
        markdown += `- Quantity: ${item.quantity} ${item.unitName}\n`;
        markdown += `- Price: ${formatCurrency(item.cost)}\n\n`;
      });
    }

    if (order.membershipLineItems.length > 0) {
      markdown += `## Memberships\n`;
      order.membershipLineItems.forEach((item) => {
        const membership = memberships.find((m) => m.id === item.membershipId);
        markdown += `### ${membership?.name || item.displayName}\n`;
        markdown += `- Price: ${formatCurrency(item.cost)}\n\n`;
      });
    }

    markdown += `## Payment Summary\n`;
    markdown += `- Subtotal: ${formatCurrency(order.orderSummary.subTotal)}\n`;

    if (order.orderSummary.membershipDiscount) {
      markdown += `- Membership Discount: -${formatCurrency(
        Math.abs(order.orderSummary.membershipDiscount)
      )}\n`;
    }

    if (order.orderSummary.promoCodeDiscount) {
      markdown += `- Promo Code Discount`;
      if (order.promoCode) markdown += ` (${order.promoCode})`;
      markdown += `: -${formatCurrency(
        Math.abs(order.orderSummary.promoCodeDiscount)
      )}\n`;
    }

    if (order.orderSummary.repeatCashApplied > 0) {
      markdown += `- RepeatCash Applied: -${formatCurrency(
        order.orderSummary.repeatCashApplied
      )}\n`;
    }

    if (order.orderSummary.serviceFee > 0) {
      markdown += `- Service Fee: ${formatCurrency(
        order.orderSummary.serviceFee
      )}\n`;
    }

    if (order.orderSummary.salesTax > 0) {
      markdown += `- Tax: ${formatCurrency(order.orderSummary.salesTax)}\n`;
    }

    markdown += `\n**Total**: ${formatCurrency(
      order.orderSummary.estimatedTotal
    )}\n`;

    if (order.paymentMethod) {
      markdown += `\n## Payment Method\n`;
      if (order.paymentMethod.financedPayment) {
        const fp = order.paymentMethod.financedPayment;
        markdown += `${fp.cardType} ending in ${fp.last4}\n`;
      } else if (order.paymentMethod.nonFinancedPayment) {
        markdown += `${order.paymentMethod.nonFinancedPayment.name}\n`;
      }
    }

    if (order.refundDetails?.refundedOn) {
      const refundDate = new Date(
        order.refundDetails.refundedOn
      ).toLocaleDateString();
      markdown += `\n## Refund Information\n`;
      markdown += `This order was refunded on ${refundDate}\n`;
    }

    return markdown;
  }

  static formatMultipleOrderDetailsAsMarkdown(
    orders: OrderDetailsData[]
  ): string {
    if (orders.length === 0) {
      return "No order details found for the specified order IDs.";
    }

    let markdown = `# Order Details (${orders.length} orders)\n\n`;

    orders.forEach((orderData, index) => {
      const { order } = orderData;
      const date = new Date(order.date).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });

      markdown += `## Order ${index + 1}: #${order.orderNumber}\n`;
      markdown += `**Date**: ${date}\n`;
      if (order.platformLocationName) {
        markdown += `**Location**: ${order.platformLocationName}\n`;
      }
      markdown += `**Total**: ${formatCurrency(
        order.orderSummary.estimatedTotal
      )}\n\n`;

      if (order.packageLineItems.length > 0) {
        markdown += `### Treatments/Packages\n`;
        order.packageLineItems.forEach((item) => {
          const pkg = orderData.packages.find((p) => p.id === item.packageId);
          markdown += `- ${pkg?.name || item.displayName} (${item.quantity} ${
            item.unitName
          }): ${formatCurrency(item.cost)}\n`;
        });
        markdown += `\n`;
      }

      if (order.membershipLineItems.length > 0) {
        markdown += `### Memberships\n`;
        order.membershipLineItems.forEach((item) => {
          const membership = orderData.memberships.find(
            (m) => m.id === item.membershipId
          );
          markdown += `- ${
            membership?.name || item.displayName
          }: ${formatCurrency(item.cost)}\n`;
        });
        markdown += `\n`;
      }

      if (order.refundDetails?.refundedOn) {
        markdown += `**Refunded on ${new Date(
          order.refundDetails.refundedOn
        ).toLocaleDateString()}**\n`;
      }

      if (index < orders.length - 1) {
        markdown += `\n---\n\n`;
      }
    });

    return markdown;
  }

  static formatOrderHistorySummaryAsMarkdown(data: OrderHistoryData): string {
    let markdown = `# Order History\n\n`;
    markdown += `*Showing ${data.orders.length} of ${data.count} total orders*\n\n`;

    data.orders.forEach((order) => {
      const date = new Date(order.date || "").toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });

      markdown += `## Order #${order.orderNumber}\n`;
      markdown += `- **Date**: ${date}\n`;
      markdown += `- **Total**: ${formatCurrency(order.amount)}\n`;
      if (order.isRefunded) {
        markdown += `- **Status**: Refunded\n`;
      }
      markdown += `- **Order ID**: ${order.id}\n\n`;
    });

    return markdown;
  }

  static formatDetailedOrderHistoryAsMarkdown(
    history: OrderHistoryData,
    details: OrderDetailsData[]
  ): string {
    let markdown = `# Order History (Detailed)\n\n`;
    markdown += `*Showing detailed info for ${details.length} recent orders out of ${history.count} total*\n\n`;

    details.forEach(({ order, packages, memberships }) => {
      const date = new Date(order.date).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });

      markdown += `## Order #${order.orderNumber}\n`;
      markdown += `**Date**: ${date}\n\n`;

      if (order.packageLineItems.length > 0) {
        markdown += `### Treatments/Packages\n`;
        order.packageLineItems.forEach((item) => {
          const pkg = packages.find((p) => p.id === item.packageId);
          const name = pkg?.name || item.displayName;
          markdown += `- ${name} (${item.quantity} ${
            item.unitName
          }): ${formatCurrency(item.cost)}\n`;
        });
        markdown += `\n`;
      }

      if (order.membershipLineItems.length > 0) {
        markdown += `### Memberships\n`;
        order.membershipLineItems.forEach((item) => {
          const membership = memberships.find(
            (m) => m.id === item.membershipId
          );
          const name = membership?.name || item.displayName;
          markdown += `- ${name}: ${formatCurrency(item.cost)}\n`;
        });
        markdown += `\n`;
      }

      markdown += `### Payment Summary\n`;
      markdown += `- Subtotal: ${formatCurrency(
        order.orderSummary.subTotal
      )}\n`;
      if (order.orderSummary.membershipDiscount) {
        markdown += `- Membership Discount: -${formatCurrency(
          Math.abs(order.orderSummary.membershipDiscount)
        )}\n`;
      }
      if (order.orderSummary.promoCodeDiscount) {
        markdown += `- Promo Discount: -${formatCurrency(
          Math.abs(order.orderSummary.promoCodeDiscount)
        )}\n`;
      }
      if (order.orderSummary.repeatCashApplied > 0) {
        markdown += `- RepeatCash Applied: -${formatCurrency(
          order.orderSummary.repeatCashApplied
        )}\n`;
      }
      markdown += `- **Total**: ${formatCurrency(
        order.orderSummary.estimatedTotal
      )}\n`;

      if (order.refundDetails?.refundedOn) {
        markdown += `\n**Refunded on ${new Date(
          order.refundDetails.refundedOn
        ).toLocaleDateString()}**\n`;
      }

      markdown += `\n---\n\n`;
    });

    return markdown;
  }

  static formatOrderHistoryForBehaviorAnalysis(
    history: OrderHistoryData,
    details: OrderDetailsData[]
  ): string {
    const analysis = AdonisMarkdownService.analyzePurchasePatterns(details);

    let markdown = `# User Purchase Behavior Analysis\n\n`;

    markdown += `## Overview\n`;
    markdown += `- Total Orders: ${analysis.totalOrders}\n`;
    markdown += `- Last Order: ${analysis.lastOrderDate}\n`;
    markdown += `- Total Spent: ${formatCurrency(analysis.totalSpent)}\n`;
    markdown += `- Average Order Value: ${formatCurrency(
      analysis.avgOrderValue
    )}\n\n`;

    if (analysis.frequentItems.length > 0) {
      markdown += `## Frequently Purchased Items\n`;
      analysis.frequentItems.forEach((item) => {
        markdown += `- **${item.name}**: ${item.count} times (last: ${item.lastPurchase})\n`;
      });
      markdown += `\n`;
    }

    markdown += `## Purchase Patterns\n`;
    markdown += `- Purchase Frequency: Every ${analysis.avgDaysBetweenOrders} days\n`;
    if (analysis.preferredPackageSizes.length > 0) {
      markdown += `- Preferred Package Sizes: ${analysis.preferredPackageSizes.join(
        ", "
      )}\n`;
    }
    markdown += `- Spending Trend: ${analysis.spendingTrend}\n\n`;

    if (analysis.activeMemberships.length > 0) {
      markdown += `## Active Memberships\n`;
      analysis.activeMemberships.forEach((m) => {
        markdown += `- ${m.name} (active since ${m.startDate})\n`;
      });
      markdown += `\n`;
    }

    if (analysis.treatmentCycles.length > 0) {
      markdown += `## Treatment Cycles\n`;
      analysis.treatmentCycles.forEach((cycle) => {
        markdown += `### ${cycle.treatment}\n`;
        markdown += `- Frequency: Every ${cycle.frequency} months\n`;
        markdown += `- Last Purchase: ${cycle.lastPurchase}\n`;
        markdown += `- Due for Renewal: ${
          cycle.isDue ? "**Yes** (overdue)" : "No"
        }\n\n`;
      });
    }

    markdown += `## Recommendations Context\n`;
    markdown += `Based on this analysis:\n`;
    markdown += `- User prefers regular treatments with consistent timing\n`;
    markdown += `- Budget range: ${formatCurrency(
      analysis.avgOrderValue - 100
    )} - ${formatCurrency(analysis.avgOrderValue + 200)}\n`;
    if (analysis.activeMemberships.length > 0) {
      markdown += `- Has active memberships (avoid duplicate membership recommendations)\n`;
    }
    if (analysis.treatmentCycles.some((c) => c.isDue)) {
      markdown += `- Some treatments are due for renewal (good upsell opportunity)\n`;
    }

    return markdown;
  }

  private static analyzePurchasePatterns(
    orders: OrderDetailsData[]
  ): PurchaseAnalysis {
    if (orders.length === 0) {
      return {
        frequentItems: [],
        avgOrderValue: 0,
        avgDaysBetweenOrders: 0,
        preferredPackageSizes: [],
        activeMemberships: [],
        treatmentCycles: [],
        totalSpent: 0,
        spendingTrend: "stable",
        lastOrderDate: "Never",
        totalOrders: 0,
      };
    }

    const totalSpent = orders.reduce(
      (sum, o) => sum + o.order.orderSummary.estimatedTotal,
      0
    );
    const avgOrderValue = totalSpent / orders.length;

    const itemCounts = new Map<
      string,
      {
        name: string;
        count: number;
        lastDate: string;
        type: "package" | "membership";
      }
    >();

    orders.forEach(({ order, packages, memberships }) => {
      order.packageLineItems.forEach((item) => {
        const pkg = packages.find((p) => p.id === item.packageId);
        const name = pkg?.name || item.displayName;
        const existing = itemCounts.get(item.packageId) || {
          name,
          count: 0,
          lastDate: order.date,
          type: "package" as const,
        };
        existing.count++;
        if (new Date(order.date) > new Date(existing.lastDate)) {
          existing.lastDate = order.date;
        }
        itemCounts.set(item.packageId, existing);
      });

      order.membershipLineItems.forEach((item) => {
        const membership = memberships.find((m) => m.id === item.membershipId);
        const name = membership?.name || item.displayName;
        const existing = itemCounts.get(item.membershipId) || {
          name,
          count: 0,
          lastDate: order.date,
          type: "membership" as const,
        };
        existing.count++;
        if (new Date(order.date) > new Date(existing.lastDate)) {
          existing.lastDate = order.date;
        }
        itemCounts.set(item.membershipId, existing);
      });
    });

    const frequentItems = Array.from(itemCounts.entries())
      .map(([id, data]) => ({
        id,
        name: data.name,
        count: data.count,
        lastPurchase: new Date(data.lastDate).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }),
        type: data.type,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    const sortedOrders = orders.sort(
      (a, b) =>
        new Date(b.order.date).getTime() - new Date(a.order.date).getTime()
    );
    let totalDays = 0;
    for (let i = 0; i < sortedOrders.length - 1; i++) {
      const days = Math.abs(
        (new Date(sortedOrders[i].order.date).getTime() -
          new Date(sortedOrders[i + 1].order.date).getTime()) /
          (1000 * 60 * 60 * 24)
      );
      totalDays += days;
    }
    const avgDaysBetweenOrders =
      sortedOrders.length > 1
        ? Math.round(totalDays / (sortedOrders.length - 1))
        : 0;

    const packageSizes = new Map<string, number>();
    orders.forEach(({ order }) => {
      order.packageLineItems.forEach((item) => {
        const size = `${item.quantity} ${item.unitName}`;
        packageSizes.set(size, (packageSizes.get(size) || 0) + 1);
      });
    });
    const preferredPackageSizes = Array.from(packageSizes.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([size]) => size);

    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    const activeMemberships = frequentItems
      .filter(
        (item) =>
          item.type === "membership" &&
          new Date(item.lastPurchase) > sixMonthsAgo
      )
      .map((item) => ({
        id: item.id,
        name: item.name,
        startDate: item.lastPurchase,
      }));

    const treatmentCycles = frequentItems
      .filter((item) => item.type === "package" && item.count >= 2)
      .map((item) => {
        const frequency = Math.round(avgDaysBetweenOrders / 30);
        const lastPurchaseDate = new Date(item.lastPurchase);
        const expectedNextDate = new Date(lastPurchaseDate);
        expectedNextDate.setMonth(expectedNextDate.getMonth() + frequency);
        const isDue = expectedNextDate < new Date();

        return {
          treatment: item.name,
          frequency,
          lastPurchase: item.lastPurchase,
          isDue,
        };
      });

    const recentOrders = sortedOrders.slice(
      0,
      Math.ceil(sortedOrders.length / 2)
    );
    const olderOrders = sortedOrders.slice(Math.ceil(sortedOrders.length / 2));
    const recentAvg =
      recentOrders.reduce(
        (sum, o) => sum + o.order.orderSummary.estimatedTotal,
        0
      ) / (recentOrders.length || 1);
    const olderAvg =
      olderOrders.reduce(
        (sum, o) => sum + o.order.orderSummary.estimatedTotal,
        0
      ) / (olderOrders.length || 1);

    let spendingTrend: "increasing" | "stable" | "decreasing" = "stable";
    if (recentAvg > olderAvg * 1.2) spendingTrend = "increasing";
    else if (recentAvg < olderAvg * 0.8) spendingTrend = "decreasing";

    return {
      frequentItems,
      avgOrderValue,
      avgDaysBetweenOrders,
      preferredPackageSizes,
      activeMemberships,
      treatmentCycles,
      totalSpent,
      spendingTrend,
      lastOrderDate: new Date(sortedOrders[0].order.date).toLocaleDateString(
        "en-US",
        {
          month: "short",
          day: "numeric",
          year: "numeric",
        }
      ),
      totalOrders: orders.length,
    };
  }
}
